import { state } from './state.js';

const PHOTO_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days in ms

/**
 * LRU Cache with optional localStorage persistence and TTL
 * - Default: sessionStorage, no TTL (dies with the tab)
 * - persistent: true → localStorage + TTL (safe for photo refs, not business data)
 */
class LRUCache {
	constructor(name, maxSize = 100, { persistent = false, ttl = null } = {}) {
		this.name = name;
		this.cache = new Map();
		this.maxSize = maxSize;
		this.store = persistent ? localStorage : sessionStorage;
		this.ttl = ttl;
		this.loadFromStorage();
	}

	loadFromStorage() {
		try {
			const stored = this.store.getItem(`bcc_cache_${this.name}`);
			if (stored) {
				const data = JSON.parse(stored);
				if (Array.isArray(data)) {
					const now = Date.now();
					const valid = this.ttl
						? data.filter(([, v]) => v && v.__ts && (now - v.__ts) < this.ttl)
						: data;
					this.cache = new Map(valid);
				}
			}
		} catch (e) {
			console.warn(`Failed to load cache ${this.name} from storage`, e);
		}
	}

	saveToStorage() {
		try {
			const entries = Array.from(this.cache.entries()).slice(-50);
			this.store.setItem(`bcc_cache_${this.name}`, JSON.stringify(entries));
		} catch (e) {
			console.warn(`Failed to save cache ${this.name} to storage`, e);
		}
	}

	get(key) {
		if (!this.cache.has(key)) return undefined;
		const entry = this.cache.get(key);
		if (this.ttl) {
			if (!entry.__ts || (Date.now() - entry.__ts) >= this.ttl) {
				this.cache.delete(key);
				return undefined;
			}
			this.cache.delete(key);
			this.cache.set(key, entry);
			return entry.__data;
		}
		this.cache.delete(key);
		this.cache.set(key, entry);
		return entry;
	}

	set(key, value) {
		const entry = this.ttl ? { __ts: Date.now(), __data: value } : value;
		if (this.cache.has(key)) this.cache.delete(key);
		if (this.cache.size >= this.maxSize) this.cache.delete(this.cache.keys().next().value);
		this.cache.set(key, entry);
		this.saveToStorage();
	}

	has(key) {
		if (!this.cache.has(key)) return false;
		if (this.ttl) {
			const entry = this.cache.get(key);
			if (!entry.__ts || (Date.now() - entry.__ts) >= this.ttl) {
				this.cache.delete(key);
				return false;
			}
		}
		return true;
	}
}

const placeDataCache = new LRUCache('place_data', 100);
const descriptionCache = new LRUCache('description', 100);
// Photo references are TOS-safe to cache — they're tokens, not business content
const photoCache = new LRUCache('photos', 200, { persistent: true, ttl: PHOTO_CACHE_TTL });

/**
 * API Request Queue & Cache
 */
const apiQueue = {
	queue: [],
	active: 0,
	maxConcurrent: 3,
	pendingFetches: new Map(), // Track in-flight requests to prevent duplicates
};

/**
 * Queue processor for API requests
 */
export function processApiQueue() {
	if (apiQueue.active >= apiQueue.maxConcurrent || apiQueue.queue.length === 0) {
		return;
	}

	const { placeId, fields, resolve, reject } = apiQueue.queue.shift();
	apiQueue.active++;

	const url = `/wp-content/plugins/geojson-generator/get-place-details.php?fields=${fields}&place_id=${placeId}`;

	fetch(url)
		.then(response => {
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			return response.json();
		})
		.then(data => {
			const cacheKey = `${placeId}_${fields}`;
			placeDataCache.set(cacheKey, data);
			if (fields === 'photo') photoCache.set(cacheKey, data);
			apiQueue.pendingFetches.delete(cacheKey);
			resolve(data);
		})
		.catch(error => {
			const cacheKey = `${placeId}_${fields}`;
			apiQueue.pendingFetches.delete(cacheKey);
			reject(error);
		})
		.finally(() => {
			apiQueue.active--;
			processApiQueue(); // Process next in queue
		});
}

export async function placeFetchData(placeId = null, fields = 'basic') {
	if (placeId === null) {
		return;
	}

	const cacheKey = `${placeId}_${fields}`;

	// Photo refs: check persistent localStorage cache first
	if (fields === 'photo' && photoCache.has(cacheKey)) {
		return photoCache.get(cacheKey);
	}

	// All other types: check session cache
	if (placeDataCache.has(cacheKey)) {
		return placeDataCache.get(cacheKey);
	}

	// Check if request is already in flight
	if (apiQueue.pendingFetches.has(cacheKey)) {
		return apiQueue.pendingFetches.get(cacheKey);
	}

	// Create new promise for this request
	const promise = new Promise((resolve, reject) => {
		apiQueue.queue.push({ placeId, fields, resolve, reject });
		processApiQueue();
	});

	apiQueue.pendingFetches.set(cacheKey, promise);
	return promise;
}

export async function placeFetchDescription(postId = null) {
	if (postId === null) {
		return;
	}

	// Check cache
	if (descriptionCache.has(postId)) {
		return descriptionCache.get(postId);
	}

	const options = {
		method: 'GET',
		mode: 'cors',
		cache: 'default',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json'
		},
		redirect: 'follow',
		referrerPolicy: 'no-referrer'
	};

	const url = `/wp-json/wp/v2/location/?include[]=${postId}&_fields[]=acf.location_description`;
	
	try {
		let response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		const data = await response.json();
		descriptionCache.set(postId, data);
		return data;
	} catch (e) {
		console.error('Fetch description failed:', e);
		throw e;
	}
}

export async function fetchGeoJson(url = '') {
	if (url === '') {
		return
	}
	
	// Check session storage for large GeoJSON files (like online-only results)
	const cacheKey = `bcc_geojson_${url}`;
	const cached = sessionStorage.getItem(cacheKey);
	if (cached) {
		try {
			return JSON.parse(cached);
		} catch(e) {}
	}

	const response = await fetch(url, {
		method: 'GET',
		mode: 'cors',
		cache: 'default',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json'
		},
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
	});
	
	const data = await response.json();
	// Cache for 5 minutes (simple session-based cache)
	try {
		sessionStorage.setItem(cacheKey, JSON.stringify(data));
	} catch(e) {}
	
	return data;
}

// Collect all selected interest IDs
const getInterestIDs = () => {
	const items = document.querySelectorAll('#interests-card-wrapper [data-post-id]');
	const ids = [];

	items.forEach(item => {
		const id = item.getAttribute('data-post-id');
		if (id) ids.push(Number(id));
	});

	return ids;
};

export async function sendEmail(url = "", nonce = "", email = "") {
	const postIDs = getInterestIDs();

	const formData = new FormData();
	formData.append('post_id', JSON.stringify(postIDs));
	formData.append('nonce', nonce);
	formData.append('email', email);
	formData.append('action', 'email_action');
	
	// Try to find Turnstile response in the DOM
	const turnstileResponse = document.querySelector('[name="cf-turnstile-response"]')?.value;
	if (turnstileResponse) {
		formData.append('turnstile_token', turnstileResponse);
	}

	const response = await fetch(url, {
		method: 'POST',
		mode: 'cors',
		cache: 'no-cache',
		credentials: 'same-origin',
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
		body: formData
	});

	return response.json();
}

export function loadOnlineBuisnesses(){
	fetchGeoJson('/wp-content/plugins/geojson-generator/results-online-only.json').then(data => {
		if(typeof data.businesses === 'undefined'){
            state.onlineOnlyBusinesses.businesses = [];
			return;
		}
		
		// Use a local array to avoid triggering state updates mid-loop if necessary
		const tempBusinesses = [];
		for (const business of data.businesses) {
			if (typeof business.is_chamber_member === 'undefined') {
				business.is_chamber_member = false;
				if (business.social_media_links && business.social_media_links.chamber) {
					business.is_chamber_member = true;
				}
			}
			tempBusinesses.push(business);
		}
		state.onlineOnlyBusinesses.businesses = tempBusinesses;
	}).catch(error => {
		console.error('Error loading online businesses:', error);
	});
}
